import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { finalize, map, Observable, shareReplay, tap } from 'rxjs';
import { API_BASE_URL } from '../../../shared/config/api.config';
import { AuthSessionService } from '../../../core/auth/auth-session.service';
import {
  AdminAccountLookupRequest,
  AdminAccountLookupResponse,
  ApiResponse,
  GrantAdminAccessRequest,
  GrantAdminAccessResponse,
  LoginAdminRequest,
  LoginAdminResponseApi,
  MessageResponse,
  RefreshResponseApi,
  RegisterAdminRequest,
  TokenApi,
} from '../models/auth-api.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly session = inject(AuthSessionService);
  private readonly baseUrl = API_BASE_URL;

  private refreshInProgress$: Observable<TokenApi> | null = null;

  loginAdmin(request: LoginAdminRequest): Observable<LoginAdminResponseApi> {
    return this.http
      .post<ApiResponse<LoginAdminResponseApi>>(`${this.baseUrl}/auth/login/admin`, request)
      .pipe(map((response) => response.data));
  }

  registerAdmin(request: RegisterAdminRequest): Observable<MessageResponse> {
    return this.http
      .post<ApiResponse<MessageResponse>>(`${this.baseUrl}/auth/register/admin`, request)
      .pipe(map((response) => response.data));
  }

  lookupAdminAccount(request: AdminAccountLookupRequest): Observable<AdminAccountLookupResponse> {
    return this.http
      .post<ApiResponse<AdminAccountLookupResponse>>(`${this.baseUrl}/auth/admin/accounts/lookup`, request)
      .pipe(map((response) => response.data));
  }

  grantAdminAccess(request: GrantAdminAccessRequest): Observable<GrantAdminAccessResponse> {
    return this.http
      .post<ApiResponse<GrantAdminAccessResponse>>(`${this.baseUrl}/auth/admin/accounts/grant`, request)
      .pipe(map((response) => response.data));
  }

  // Deduplica refreshes concurrentes: varias requests en 401 al mismo tiempo
  // comparten esta misma llamada en vez de disparar un /auth/admin/refresh cada una.
  refreshToken(): Observable<TokenApi> {
    if (!this.refreshInProgress$) {
      this.refreshInProgress$ = this.http
        .post<ApiResponse<RefreshResponseApi>>(`${this.baseUrl}/auth/admin/refresh`, {})
        .pipe(
          map((response) => response.data.token),
          tap((token) => this.session.updateTokens(token)),
          finalize(() => (this.refreshInProgress$ = null)),
          shareReplay(1),
        );
    }
    return this.refreshInProgress$;
  }

  logout(): Observable<void> {
    return this.http
      .post<ApiResponse<void>>(`${this.baseUrl}/auth/admin/logout`, {})
      .pipe(map(() => undefined));
  }
}
